export type RadioPlan = 'free' | 'pro' | 'premium';
export type RadioStatus = 'activa' | 'inactiva' | 'suspendida';
export type RadioCategory = 'musical' | 'noticias' | 'deportiva' | 'cultural' | 'religiosa' | 'comunitaria' | 'juvenil' | 'variada';

export interface ExploreRadio {
  id: number;
  name: string;
  frequency: string;
  slogan: string;
  plan: RadioPlan;
  status: RadioStatus;
  category: RadioCategory;
  location: string;
  country: string;
  listeners: number;
  peakListeners: number;
  rating: number;
  totalPrograms: number;
  totalLives: number;
  totalPosts: number;
  imageUrl: string;
  owner: string;
  createdAt: string;
  lastActive: string;
}

export const planLabels: Record<RadioPlan, string> = {
  free: 'Free',
  pro: 'Pro',
  premium: 'Premium',
};

export const planColors: Record<RadioPlan, string> = {
  free: 'bg-foreground-200 text-foreground-700',
  pro: 'bg-primary-100 text-primary-700',
  premium: 'bg-accent-100 text-accent-700',
};

export const statusLabels: Record<RadioStatus, string> = {
  activa: 'Activa',
  inactiva: 'Inactiva',
  suspendida: 'Suspendida',
};

export const statusColors: Record<RadioStatus, string> = {
  activa: 'bg-emerald-100 text-emerald-700',
  inactiva: 'bg-foreground-200 text-foreground-600',
  suspendida: 'bg-red-100 text-red-700',
};

export const categoryLabels: Record<RadioCategory, string> = {
  musical: 'Musical',
  noticias: 'Noticias',
  deportiva: 'Deportiva',
  cultural: 'Cultural',
  religiosa: 'Religiosa',
  comunitaria: 'Comunitaria',
  juvenil: 'Juvenil',
  variada: 'Variada',
};

export const exploreRadios: ExploreRadio[] = [
  {
    id: 1,
    name: 'Radio Estelar',
    frequency: '92.3 RFW',
    slogan: 'La que suena en tu corazón',
    plan: 'premium',
    status: 'activa',
    category: 'musical',
    location: 'Buenos Aires',
    country: 'Argentina',
    listeners: 8450,
    peakListeners: 12300,
    rating: 4.7,
    totalPrograms: 18,
    totalLives: 42,
    totalPosts: 215,
    imageUrl: 'https://readdy.ai/api/search-image?query=Modern%20radio%20station%20studio%20interior%20with%20professional%20broadcast%20equipment%2C%20warm%20ambient%20lighting%2C%20empty%20studio%20ready%20for%20broadcast%2C%20sleek%20contemporary%20design%20with%20acoustic%20panels%2C%20professional%20microphone%20on%20desk%2C%20radio%20frequency%20digital%20display%20visible&width=400&height=300&seq=explore-radio-1&orientation=landscape',
    owner: 'Martín Ferreira',
    createdAt: '2025-03-15',
    lastActive: '2026-06-08T14:22:00',
  },
  {
    id: 2,
    name: 'FM Vibración',
    frequency: '101.7 RFW',
    slogan: 'Pura energía positiva',
    plan: 'pro',
    status: 'activa',
    category: 'juvenil',
    location: 'Santiago',
    country: 'Chile',
    listeners: 5120,
    peakListeners: 8900,
    rating: 4.4,
    totalPrograms: 12,
    totalLives: 28,
    totalPosts: 178,
    imageUrl: 'https://readdy.ai/api/search-image?query=Vibrant%20youth%20oriented%20radio%20station%20studio%20with%20colorful%20lighting%2C%20modern%20DJ%20setup%20with%20turntables%20visible%2C%20energetic%20atmosphere%2C%20graffiti%20art%20wall%20decoration%2C%20digital%20streaming%20monitors%2C%20urban%20contemporary%20radio%20aesthetic&width=400&height=300&seq=explore-radio-2&orientation=landscape',
    owner: 'Valentina Rojas',
    createdAt: '2025-06-20',
    lastActive: '2026-06-08T13:10:00',
  },
  {
    id: 3,
    name: 'Radio Noticias YA',
    frequency: '540 RFW',
    slogan: 'La verdad primero',
    plan: 'pro',
    status: 'activa',
    category: 'noticias',
    location: 'Lima',
    country: 'Perú',
    listeners: 12300,
    peakListeners: 18900,
    rating: 4.8,
    totalPrograms: 24,
    totalLives: 56,
    totalPosts: 432,
    imageUrl: 'https://readdy.ai/api/search-image?query=Professional%20news%20radio%20station%20studio%2C%20multiple%20screens%20showing%20news%20feeds%20and%20breaking%20news%20alerts%2C%20serious%20journalism%20atmosphere%2C%20modern%20broadcast%20desk%20with%20multiple%20microphones%2C%20world%20map%20background%2C%20press%20room%20aesthetic&width=400&height=300&seq=explore-radio-3&orientation=landscape',
    owner: 'Ricardo Suárez',
    createdAt: '2025-01-10',
    lastActive: '2026-06-08T15:45:00',
  },
  {
    id: 4,
    name: 'Bossa Nova Brasil',
    frequency: '96.5 RFW',
    slogan: 'O som do Brasil',
    plan: 'free',
    status: 'activa',
    category: 'musical',
    location: 'São Paulo',
    country: 'Brasil',
    listeners: 2100,
    peakListeners: 4500,
    rating: 4.2,
    totalPrograms: 6,
    totalLives: 8,
    totalPosts: 45,
    imageUrl: 'https://readdy.ai/api/search-image?query=Brazilian%20music%20radio%20station%20studio%2C%20bossa%20nova%20themed%20decor%20with%20warm%20tropical%20colors%2C%20acoustic%20guitar%20on%20display%2C%20relaxed%20intimate%20atmosphere%2C%20wooden%20design%20elements%2C%20vinyl%20records%20collection%20visible%2C%20cozy%20broadcast%20space&width=400&height=300&seq=explore-radio-4&orientation=landscape',
    owner: 'Ana Oliveira',
    createdAt: '2026-02-01',
    lastActive: '2026-06-07T22:30:00',
  },
  {
    id: 5,
    name: 'Radio Deportiva Total',
    frequency: '890 RFW',
    slogan: 'Pasión por el deporte',
    plan: 'premium',
    status: 'activa',
    category: 'deportiva',
    location: 'Montevideo',
    country: 'Uruguay',
    listeners: 6700,
    peakListeners: 14500,
    rating: 4.5,
    totalPrograms: 15,
    totalLives: 38,
    totalPosts: 290,
    imageUrl: 'https://readdy.ai/api/search-image?query=Sports%20radio%20station%20studio%2C%20sports%20memorabilia%20on%20walls%2C%20multiple%20sports%20jerseys%20framed%2C%20dynamic%20energetic%20atmosphere%2C%20broadcast%20desk%20with%20sports%20themed%20decor%2C%20large%20screens%20showing%20match%20highlights%2C%20commentator%20booth%20aesthetic&width=400&height=300&seq=explore-radio-5&orientation=landscape',
    owner: 'Gonzalo Pérez',
    createdAt: '2025-05-12',
    lastActive: '2026-06-08T16:00:00',
  },
  {
    id: 6,
    name: 'Cultura Viva Radio',
    frequency: '104.1 RFW',
    slogan: 'Donde las ideas florecen',
    plan: 'free',
    status: 'activa',
    category: 'cultural',
    location: 'Ciudad de México',
    country: 'México',
    listeners: 3800,
    peakListeners: 6200,
    rating: 4.6,
    totalPrograms: 10,
    totalLives: 15,
    totalPosts: 120,
    imageUrl: 'https://readdy.ai/api/search-image?query=Cultural%20radio%20station%20studio%2C%20bookshelves%20filled%20with%20literature%2C%20classical%20artwork%20on%20walls%2C%20warm%20intellectual%20atmosphere%2C%20vintage%20microphone%20collection%20display%2C%20elegant%20wooden%20broadcast%20furniture%2C%20scholarly%20refined%20aesthetic&width=400&height=300&seq=explore-radio-6&orientation=landscape',
    owner: 'Alejandra Montes',
    createdAt: '2025-08-03',
    lastActive: '2026-06-08T10:15:00',
  },
  {
    id: 7,
    name: 'Radio Evangelio Vivo',
    frequency: '1120 RFW',
    slogan: 'Llevando la palabra',
    plan: 'pro',
    status: 'activa',
    category: 'religiosa',
    location: 'Bogotá',
    country: 'Colombia',
    listeners: 9200,
    peakListeners: 15000,
    rating: 4.3,
    totalPrograms: 8,
    totalLives: 22,
    totalPosts: 95,
    imageUrl: 'https://readdy.ai/api/search-image?query=Religious%20radio%20station%20studio%2C%20serene%20peaceful%20atmosphere%2C%20soft%20natural%20lighting%20through%20windows%2C%20simple%20elegant%20broadcast%20setup%2C%20wooden%20cross%20decoration%20subtle%2C%20warm%20spiritual%20ambiance%2C%20community%20focused%20broadcast%20space&width=400&height=300&seq=explore-radio-7&orientation=landscape',
    owner: 'Pastor Luis Cárdenas',
    createdAt: '2025-04-18',
    lastActive: '2026-06-08T08:30:00',
  },
  {
    id: 8,
    name: 'Radio Mix Latina',
    frequency: '99.9 RFW',
    slogan: 'El ritmo que nos une',
    plan: 'premium',
    status: 'activa',
    category: 'variada',
    location: 'Miami',
    country: 'Estados Unidos',
    listeners: 11100,
    peakListeners: 19200,
    rating: 4.9,
    totalPrograms: 20,
    totalLives: 48,
    totalPosts: 380,
    imageUrl: 'https://readdy.ai/api/search-image?query=Latin%20music%20radio%20station%20studio%2C%20vibrant%20colorful%20decor%20with%20tropical%20accents%2C%20modern%20broadcast%20console%2C%20high%20energy%20atmosphere%2C%20percussion%20instruments%20as%20decoration%2C%20digital%20streaming%20setup%2C%20contemporary%20multicultural%20radio%20aesthetic&width=400&height=300&seq=explore-radio-8&orientation=landscape',
    owner: 'Carolina Herrera',
    createdAt: '2025-02-14',
    lastActive: '2026-06-08T17:00:00',
  },
  {
    id: 9,
    name: 'Community Sound',
    frequency: '97.3 RFW',
    slogan: 'La voz del barrio',
    plan: 'free',
    status: 'inactiva',
    category: 'comunitaria',
    location: 'Medellín',
    country: 'Colombia',
    listeners: 0,
    peakListeners: 3400,
    rating: 3.9,
    totalPrograms: 4,
    totalLives: 3,
    totalPosts: 18,
    imageUrl: 'https://readdy.ai/api/search-image?query=Small%20community%20radio%20station%20studio%2C%20local%20neighborhood%20themed%20decor%2C%20modest%20but%20cozy%20broadcast%20setup%2C%20community%20bulletin%20board%20on%20wall%2C%20warm%20welcoming%20atmosphere%2C%20grassroots%20media%20space%2C%20intimate%20local%20broadcast%20booth&width=400&height=300&seq=explore-radio-9&orientation=landscape',
    owner: 'Colectivo Barrial',
    createdAt: '2025-11-20',
    lastActive: '2026-05-15T09:00:00',
  },
  {
    id: 10,
    name: 'Radio Extremo',
    frequency: '93.7 RFW',
    slogan: 'Sin límites, sin censura',
    plan: 'free',
    status: 'suspendida',
    category: 'juvenil',
    location: 'Asunción',
    country: 'Paraguay',
    listeners: 0,
    peakListeners: 7800,
    rating: 2.1,
    totalPrograms: 7,
    totalLives: 12,
    totalPosts: 67,
    imageUrl: 'https://readdy.ai/api/search-image?query=Edgy%20alternative%20radio%20station%20studio%2C%20underground%20punk%20rock%20aesthetic%2C%20dark%20moody%20lighting%2C%20band%20posters%20covering%20walls%2C%20raw%20authentic%20broadcast%20setup%2C%20rebellious%20counter%20culture%20atmosphere%2C%20cassette%20tapes%20and%20vintage%20gear%20visible&width=400&height=300&seq=explore-radio-10&orientation=landscape',
    owner: 'Javier Insfrán',
    createdAt: '2025-09-05',
    lastActive: '2026-04-30T18:00:00',
  },
  {
    id: 11,
    name: 'Tango FM',
    frequency: '95.1 RFW',
    slogan: 'El dos por cuatro del alma',
    plan: 'pro',
    status: 'activa',
    category: 'cultural',
    location: 'Rosario',
    country: 'Argentina',
    listeners: 4800,
    peakListeners: 7600,
    rating: 4.5,
    totalPrograms: 9,
    totalLives: 18,
    totalPosts: 134,
    imageUrl: 'https://readdy.ai/api/search-image?query=Tango%20music%20radio%20station%20studio%2C%20classic%20vintage%20argentine%20decor%2C%20antique%20radio%20equipment%20display%2C%20warm%20nostalgic%20lighting%2C%20bandoneon%20instrument%20as%20centerpiece%2C%20elegant%20old%20world%20broadcast%20atmosphere%2C%20traditional%20porte%C3%B1o%20cultural%20aesthetic&width=400&height=300&seq=explore-radio-11&orientation=landscape',
    owner: 'Roberto Avalos',
    createdAt: '2025-07-10',
    lastActive: '2026-06-08T12:00:00',
  },
  {
    id: 12,
    name: 'Radio Marítima',
    frequency: '88.5 RFW',
    slogan: 'Navegando en tus oídos',
    plan: 'free',
    status: 'activa',
    category: 'variada',
    location: 'Valparaíso',
    country: 'Chile',
    listeners: 1500,
    peakListeners: 3200,
    rating: 4.0,
    totalPrograms: 5,
    totalLives: 6,
    totalPosts: 32,
    imageUrl: 'https://readdy.ai/api/search-image?query=Coastal%20themed%20radio%20station%20studio%2C%20nautical%20decor%20with%20maritime%20artifacts%2C%20ocean%20view%20window%20visible%2C%20relaxed%20seaside%20atmosphere%2C%20wooden%20ship%20wheel%20decoration%2C%20lighthouse%20model%20on%20shelf%2C%20calm%20coastal%20broadcast%20aesthetic&width=400&height=300&seq=explore-radio-12&orientation=landscape',
    owner: 'Pedro Núñez',
    createdAt: '2026-01-15',
    lastActive: '2026-06-07T20:00:00',
  },
];